/** @jsxImportSource @emotion/react */
import { PrinterOutlined } from '@ant-design/icons'
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { useRegistrationData } from '@sportninja/common/hooks/useRegistrationData'
import { useSports } from '@sportninja/common/hooks/useSports'
import { t } from '@sportninja/common/i18n'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import {
  getAllGenericSportProperties,
  getHockeySport,
  getSoccerSport,
  getSports,
} from '@sportninja/common/selectors/types'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { Button, Dropdown } from 'antd'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import GeneratePluginModal from 'src/components/GeneralPluginModal'
import { useSportsPayCallbackEffect } from 'src/features/account/sportsPaySignUp'
import ImportCSV from 'src/components/ImportCSV'
import ImportOfficials from 'src/components/ImportCSV/ImportOfficials'
import OfficialListTable from 'src/components/List/OfficialListTable'
import UserListTable from 'src/components/List/UserListTable'
import NewDaySmartForm from 'src/components/NewDaySmartForm'
import { NewModal } from 'src/components/NewModal'
import LocationModal from 'src/components/LocationModal'
import OrganizationModal from 'src/components/OrganizationModal'
import ScheduleModal from 'src/components/ScheduleModal'
import TeamModal from 'src/components/TeamModal'
import { ScheduleReprocessingContext } from 'src/components/ScheduleReprocessingContext'
import { font } from 'src/components/css'
import { useSelector } from 'src/hooks/useRedux'
import FileDownload from '../../components/FileDownload'
import FormSheet from '../../components/Form/FormSheet'
import banners from '../../components/Form/banner-helpers'
import { feedItemForm, teamForm } from '../../components/Form/form-configs'
import ImportTeamRoster from '../../components/ImportCSV/ImportTeamRoster'
import ExportIcon from 'src/components/icons/ExportIcon'
import ImportIcon from 'src/components/icons/ImportIcon'
import { AddOfficial } from '../../components/List/OfficialList'
import UserModal from '../../components/UserModal'
import NewReports from '../../components/NewReports'
import Page from '../../components/Page'
import BaseActionButton from '../../components/Page/BaseActionButton'
import actionDropdownOverlay from '../../components/Page/actionDropdownOverlay'
import EditButton from '../../components/Page/EditButton'
import FavouriteButton from '../../components/Page/FavouriteButton'
import NewButton from '../../components/Page/NewButton'
import { Mobile } from '../../components/Responsive'
import Venues from '../../components/Venues'
import OrgListTable from '../Orgs/OrgListTable'
import RegistrationSetup from '../Schedule/RegistrationSetup'
import ReprocessingSettingsOverlay from '../Schedule/ReprocessingSettingsOverlay'
import ScheduleListTable from '../Schedules/ScheduleListTable'
import TeamListTable from '../Teams/TeamListTable'
import { getBreadcrumbs, useReadItem, useStoreRecent } from '../utils'
import EmptyOrg from './EmptyOrg'
import daysmartIcon from './daysmart-icon.png'
import CompetitionSetupModal from 'src/components/CompetitionSetupModal'
import CreateCompetitionWizard from 'src/features/competition/wizard/CreateCompetitionWizard'
import CouponsPage from '../coupons'
import EntityFeed from 'src/components/EntityFeed'
import OrgSchedule from 'src/components/v2/OrgSchedule'
import PrintSignSheetsModal from 'src/components/PrintSignInSheetsModal'
import OrgLeaderboards from 'src/components/v2/OrgLeaderboards'

// Reducer for managing organization page state
const initialOrgState = {
  // Modal states
  isScheduleModalOpen: false,
  isCreateWizardOpen: false,
  isLocationModalOpen: false,
  isTeamModalOpen: false,
  isCompetitionSetupModalOpen: false,
  isRegistrationFormOpen: false,
  organizationModalOpen: false,

  // Selection states
  selectedSchedule: null,
  selectedSubOrg: null,
  selectedTeam: null,

  // Form data
  newCompetitionId: null,
  registrationScheduleId: null,
  scheduleInitialState: null,
  scheduleInitialStateId: null,
  cloneInitialSchedule: null,
  organization: null,

  // Data
  entityCount: null,
}

const orgReducer = (state, action) => {
  switch (action.type) {
    case 'SET_SCHEDULE_MODAL_OPEN':
      return { ...state, isScheduleModalOpen: action.payload }

    case 'SET_CREATE_WIZARD_OPEN':
      return {
        ...state,
        isCreateWizardOpen: action.payload,
        // Clear any clone-source seed when the wizard closes.
        cloneInitialSchedule: action.payload
          ? state.cloneInitialSchedule
          : null,
      }

    case 'OPEN_CLONE_WIZARD':
      return {
        ...state,
        isCreateWizardOpen: true,
        cloneInitialSchedule: action.payload,
      }

    case 'SET_LOCATION_MODAL_OPEN':
      return { ...state, isLocationModalOpen: action.payload }

    case 'SET_TEAM_MODAL_OPEN':
      return { ...state, isTeamModalOpen: action.payload }

    case 'SET_COMPETITION_SETUP_MODAL_OPEN':
      return { ...state, isCompetitionSetupModalOpen: action.payload }

    case 'SET_REGISTRATION_FORM_OPEN':
      return { ...state, isRegistrationFormOpen: action.payload }

    case 'SET_ORGANIZATION_MODAL_OPEN':
      return { ...state, organizationModalOpen: action.payload }

    case 'SET_SELECTED_SCHEDULE':
      return { ...state, selectedSchedule: action.payload }

    case 'SET_SELECTED_SUB_ORG':
      return { ...state, selectedSubOrg: action.payload }

    case 'SET_SELECTED_TEAM':
      return { ...state, selectedTeam: action.payload }

    case 'SET_NEW_COMPETITION_ID':
      return { ...state, newCompetitionId: action.payload }

    case 'SET_REGISTRATION_SCHEDULE_ID':
      return { ...state, registrationScheduleId: action.payload }

    case 'SET_SCHEDULE_INITIAL_STATE':
      return { ...state, scheduleInitialState: action.payload }

    case 'SET_SCHEDULE_INITIAL_STATE_ID':
      return { ...state, scheduleInitialStateId: action.payload }

    case 'SET_ORGANIZATION':
      return { ...state, organization: action.payload }

    case 'SET_ENTITY_COUNT':
      return { ...state, entityCount: action.payload }

    case 'CLEAR_PLUGIN_SELECTIONS':
      return {
        ...state,
        selectedSchedule: null,
        selectedSubOrg: null,
        selectedTeam: null,
      }

    case 'RESET_SCHEDULE_FORM':
      return {
        ...state,
        scheduleInitialState: null,
        scheduleInitialStateId: null,
        isScheduleModalOpen:
          action.payload !== undefined
            ? action.payload
            : state.isScheduleModalOpen,
      }

    case 'RESET_ORGANIZATION_FORM':
      return {
        ...state,
        organization: null,
        organizationModalOpen:
          action.payload !== undefined
            ? action.payload
            : state.organizationModalOpen,
      }

    default:
      return state
  }
}

const Organization = ({
  match,
  isInFrame,
  soccerSport,
  hockeySport,
  readProvinces,
  genericSportsForDaysmart,
  sportsForDaysmart,
}) => {
  // ============================================================================
  // HOOKS & STATE MANAGEMENT
  // ============================================================================

  // External hooks
  const account = useSelector((state) => state.account._meAccount)
  const history = useHistory()
  const {
    loading,
    error,
    item,
    hierarchy,
    permission,
    readItem,
    refreshKey,
    triggerRefresh,
    features,
  } = useReadItem()

  // Local state management with reducer
  const [orgState, dispatch] = useReducer(orgReducer, initialOrgState)
  const {
    isScheduleModalOpen,
    isCreateWizardOpen,
    isLocationModalOpen,
    isTeamModalOpen,
    isCompetitionSetupModalOpen,
    isRegistrationFormOpen,
    organizationModalOpen,
    selectedSchedule,
    selectedSubOrg,
    selectedTeam,
    newCompetitionId,
    registrationScheduleId,
    scheduleInitialState,
    scheduleInitialStateId,
    cloneInitialSchedule,
    organization,
    entityCount,
  } = orgState

  // Custom hooks
  const { sports, sportOptions, fetchSports } = useSports()
  const { data, types, availableTaxes, refetch, fetchTypes } =
    useRegistrationData(registrationScheduleId)
  const { listeners = {}, addPending } = useContext(ScheduleReprocessingContext)

  // ============================================================================
  // REFS
  // ============================================================================

  const daySmartImportModalRef = useRef(null)
  const generatePluginTagModalRef = useRef(null)
  const refetchRegistrationFn = useRef(refetch)
  const printSignInSheetModalRef = useRef(null)
  const openCouponCreateRef = useRef(null)

  // State for visible game IDs used for user-triggered actions (print/export)
  const [visibleGameIds, setVisibleGameIds] = useState([])

  // User modal state
  const [isUserModalOpen, setIsUserModalOpen] = useState(false)
  const [editingUser, setEditingUser] = useState(null)

  // ============================================================================
  // COMPUTED VALUES & MEMOIZED VARIABLES
  // ============================================================================

  const isProfessionalOrUltimateAccount = true
  const scheduleReprocessingListener = listeners[scheduleInitialStateId]
  const [dismissed, setDismissed] = useState(false)

  // URL and routing related
  const ORG_ID = useMemo(
    () => match.params.id?.replace('?sandbox_tour=1', ''),
    [match]
  )
  const BASE_ROUTE = useMemo(() => `${ROUTES.ORG_ROOT}/${ORG_ID}`, [ORG_ID])
  const urlParams = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  )
  const sandboxTour = useMemo(() => urlParams.get('sandbox_tour'), [urlParams])
  const gettingStarted = useMemo(
    () => urlParams.get('getting_started'),
    [urlParams]
  )

  // Item-derived values
  const IS_PUBLIC = useMemo(() => item.is_public, [item])
  const syncSalesforce = useMemo(() => item?.sync_salesforce || false, [item])
  const isDaysmartConnectedAtOrgLevel = useMemo(
    () => item?.integrations?.daysmart?.is_connected === true,
    [item]
  )

  // Computed states
  const isEmptyOrg = useMemo(
    () =>
      entityCount === null ||
      (entityCount?.org_count === 0 &&
        entityCount?.team_count === 0 &&
        entityCount?.schedule_count === 0 &&
        entityCount?.venue_count === 0),
    [entityCount]
  )
  const shouldDisplayMergeButton = useMemo(() => {
    if (isEmptyOrg) {
      return false
    }
    if (isCanlan) {
      // Only display merge button for Canlan if the org is Canlan root
      return permission?.update && ORG_ID === 'H9YZBLskm0BLnlNU'
    } else {
      return permission?.update
    }
  }, [isEmptyOrg, permission?.update, ORG_ID])

  // ============================================================================
  // SIDE EFFECTS
  // ============================================================================

  // Store recent items
  useStoreRecent(ENTITY_TYPES.org, item, error, ORG_ID, isInFrame)

  // Fetch sports data
  useEffect(() => {
    if (!sports || sports?.length === 0) {
      fetchSports()
    }
  }, [sports])

  // Fetch types data
  useEffect(() => {
    if (!types || types === null) {
      fetchTypes()
    }
  }, [types])

  // Fetch registration data
  useEffect(() => {
    if (registrationScheduleId) {
      refetch()
    }
  }, [registrationScheduleId])

  // Update registration refetch function
  useEffect(() => {
    if (registrationScheduleId && data) {
      refetchRegistrationFn.current = refetch
    }
  }, [data, registrationScheduleId])

  // ============================================================================
  // EVENT HANDLERS & CALLBACKS
  // ============================================================================

  // Plugin selection handlers
  const onClickWebPluginOnScheduleList = useCallback((schedule) => {
    dispatch({ type: 'SET_SELECTED_SUB_ORG', payload: null })
    dispatch({ type: 'SET_SELECTED_TEAM', payload: null })
    dispatch({ type: 'SET_SELECTED_SCHEDULE', payload: schedule })
    generatePluginTagModalRef?.current?.openModal()
  }, [])

  const onClickWebPluginOnSubOrgList = useCallback((schedule) => {
    dispatch({ type: 'SET_SELECTED_SCHEDULE', payload: null })
    dispatch({ type: 'SET_SELECTED_TEAM', payload: null })
    dispatch({ type: 'SET_SELECTED_SUB_ORG', payload: schedule?.id })
    generatePluginTagModalRef?.current?.openModal()
  }, [])

  const onClickWebPluginOnTeamList = useCallback((schedule) => {
    dispatch({ type: 'SET_SELECTED_SCHEDULE', payload: null })
    dispatch({ type: 'SET_SELECTED_SUB_ORG', payload: null })
    dispatch({ type: 'SET_SELECTED_TEAM', payload: schedule })
    generatePluginTagModalRef?.current?.openModal()
  }, [])

  // Entity management handlers
  const readyEntityCount = useCallback(async () => {
    try {
      const { results } = await req(`/organizations/${ORG_ID}/entity-count`)
      if (results) {
        dispatch({ type: 'SET_ENTITY_COUNT', payload: results })
      }
    } catch (e) {
      console.error(e)
      dispatch({ type: 'SET_ENTITY_COUNT', payload: null })
    }
  }, [ORG_ID])

  const onAddTeam = useCallback(
    async (...args) => {
      const [values] = args
      const { imageData, backgroundImageData, ...form } = values

      form.organization_id = ORG_ID
      form.name = form.name_full

      if (typeof form.is_public === 'undefined') {
        form.is_public = IS_PUBLIC
      }

      const body = JSON.stringify(form)
      const response = await req('/teams', {
        method: 'POST',
        body,
      })

      if (imageData) {
        await req(`/teams/${response.data.id}/image`, {
          method: 'POST',
          body: imageData,
        })
      }
      if (backgroundImageData) {
        await req(`/teams/${response.data.id}/background-image`, {
          method: 'POST',
          body: backgroundImageData,
        })
      }

      triggerRefresh()
      readyEntityCount()
      banners.entityCreate(ENTITY_TYPES.team)(response)
      return response
    },
    [ORG_ID, IS_PUBLIC, triggerRefresh, readyEntityCount]
  )

  // Utility functions
  const isOrganization = useCallback((sSchedule, sTeam, sSubOrg) => {
    if (!sSchedule && !sTeam && sSubOrg) {
      return true
    }
    if (!sSchedule && !sTeam && !sSubOrg) {
      return true
    }
    return false
  }, [])

  // Modal and UI handlers
  const handleScheduleModalCancel = useCallback(() => {
    dispatch({ type: 'RESET_SCHEDULE_FORM', payload: false })
  }, [])

  const handleScheduleModalTriggerRefresh = useCallback(() => {
    readyEntityCount()
    readItem(ENTITY_TYPES.org, ORG_ID)
  }, [readyEntityCount, readItem, ORG_ID])

  /**
   * Re-reads the organization after a SportsPay registration may have advanced,
   * so `sportspay.is_onboarded` (which gates the signup button) reflects it.
   *
   * `preserveState: true` keeps the current item/permission in place while the
   * read is in flight — a bare refetch blanks them, which unmounts the header
   * and the still-open frame underneath the user.
   */
  const handleSportsPayOnboardingChanged = useCallback(() => {
    readItem(ENTITY_TYPES.org, ORG_ID, { preserveState: true })
  }, [readItem, ORG_ID])

  /*
   * Handles a SportsPay registration finished in a NEW TAB: the static callback
   * page redirects here with its params, and this records the completion. The
   * embedded path is handled by the button instead, which posts and closes.
   */
  useSportsPayCallbackEffect({
    accountId: item?.account_id,
    onOnboardingChanged: handleSportsPayOnboardingChanged,
  })

  const handlePluginModalClose = useCallback(() => {
    generatePluginTagModalRef?.current?.closeModal()
    dispatch({ type: 'CLEAR_PLUGIN_SELECTIONS' })
  }, [])

  const handleOrganizationModalRefresh = useCallback(() => {
    readyEntityCount()
    readItem(ENTITY_TYPES.org, ORG_ID)
  }, [readyEntityCount, readItem, ORG_ID])

  const handleCompetitionSetupModalContinue = useCallback(() => {
    history.push(ROUTES.SCHEDULE_ROOT + `/${newCompetitionId}/setup`)
    dispatch({ type: 'SET_NEW_COMPETITION_ID', payload: null })
  }, [history, newCompetitionId])

  const handleCompetitionSetupModalCancel = useCallback(() => {
    dispatch({ type: 'SET_NEW_COMPETITION_ID', payload: null })
  }, [])

  // ============================================================================
  // ADDITIONAL SIDE EFFECTS
  // ============================================================================

  // Load entity data
  useEffect(() => {
    if (ORG_ID) {
      readyEntityCount()
    }
  }, [ORG_ID])

  useEffect(() => {
    readItem(ENTITY_TYPES.org, ORG_ID)
  }, [ORG_ID])

  // Dropdown menu items for "NEW" button
  const items = useMemo(() => {
    const menuItems = []

    if (item?.can_contain_organizations !== false) {
      menuItems.push({
        key: '1',
        label: (
          <a
            target='_blank'
            rel='noopener noreferrer'
            onClick={() => {
              dispatch({ type: 'SET_ORGANIZATION', payload: null })
              dispatch({ type: 'SET_ORGANIZATION_MODAL_OPEN', payload: true })
            }}
          >
            {isCanlan ? 'League' : 'Organization'}
          </a>
        ),
      })
    }

    if (item?.can_contain_schedules !== false) {
      menuItems.push({
        key: '2',
        label: (
          <a
            id='schedule-tutorial'
            target='_blank'
            rel='noopener noreferrer'
            onClick={() => {
              dispatch({ type: 'SET_CREATE_WIZARD_OPEN', payload: true })
            }}
          >
            {isCanlan ? 'Season' : 'Competition'}
          </a>
        ),
      })
    }

    if (item?.can_contain_teams !== false) {
      menuItems.push({
        key: '3',
        label: (
          <a
            target='_blank'
            rel='noopener noreferrer'
            onClick={() => {
              dispatch({ type: 'SET_TEAM_MODAL_OPEN', payload: true })
            }}
            id='team-tutorial'
          >
            Team
          </a>
        ),
      })
    }

    if (item?.can_contain_locations !== false) {
      menuItems.push({
        key: '4',
        label: (
          <a
            id='location-tutorial'
            target='_blank'
            rel='noopener noreferrer'
            onClick={() => {
              dispatch({ type: 'SET_LOCATION_MODAL_OPEN', payload: true })
            }}
          >
            {isCanlan ? 'Sports Complex' : 'Location'}
          </a>
        ),
      })
    }

    return menuItems
  }, [
    IS_PUBLIC,
    permission,
    sportOptions,
    sports,
    item?.can_contain_organizations,
    item?.can_contain_schedules,
    item?.can_contain_teams,
    item?.can_contain_locations,
  ])

  // ============================================================================
  // UI COMPONENTS & RENDER PROPS
  // ============================================================================

  // Page builders - broken down for maintainability
  const buildEmptyOrgPage = useCallback(
    () => ({
      to: `${ROUTES.ORG_ROOT}/${ORG_ID}`,
      text: '',
      component: (
        <EmptyOrg
          parentId={ORG_ID}
          IS_PUBLIC={IS_PUBLIC}
          syncSalesforce={syncSalesforce}
          hockeySport={hockeySport}
          onAddTeam={onAddTeam}
          onAddSchedule={() => {
            dispatch({
              type: 'SET_SCHEDULE_MODAL_OPEN',
              payload: true,
            })
          }}
          onAddOrg={() => {
            dispatch({
              type: 'RESET_ORGANIZATION_FORM',
              payload: true,
            })
          }}
          permission={permission}
          sportOptions={sportOptions}
          sports={sports}
        />
      ),
      actions: [
        permission.admin && (
          <div id='tutorial-dropdown' key='org_dropdown_one'>
            <Dropdown
              menu={{ items }}
              placement='bottomLeft'
              trigger={['click']}
              key='org_dropdown_one'
            >
              <Button
                id='tutorial-button'
                css={css`
                  margin-left: 8px;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  width: ${64}px;
                  height: 32px;

                  border: ${isCanlan ? 2 : 1}px solid ${colors.DEFAULT_FLAIR};
                  border-radius: 4px;

                  ${font.title}
                  font-weight: 600;
                  font-size: 18px;

                  letter-spacing: 0.015em;
                  text-transform: uppercase;
                  color: white;

                  transition: border-color 0.1s ease-in-out;

                  &:hover {
                    border-color: white;
                  }
                `}
              >
                + NEW
              </Button>
            </Dropdown>
          </div>
        ),
      ],
    }),
    [
      ORG_ID,
      IS_PUBLIC,
      syncSalesforce,
      hockeySport,
      onAddTeam,
      permission,
      sportOptions,
      sports,
      items,
    ]
  )

  const buildReportsPage = useCallback(
    () =>
      !isInFrame && permission.admin
        ? {
            to: `${ROUTES.ORG_ROOT}/${ORG_ID}/reports`,
            text: 'Reports',
            privatePage: true,
            component: (
              <NewReports
                entity={item}
                entityType={ENTITY_TYPES.org}
                entityId={ORG_ID}
              />
            ),
          }
        : null,
    [isInFrame, permission.admin, ORG_ID, item]
  )

  const buildOfficialsPage = useCallback(
    () =>
      !isInFrame && permission.admin
        ? {
            to: `${ROUTES.ORG_ROOT}/${ORG_ID}/officials`,
            text: t('common:official_plural', { count: 2 }),
            component: (
              <OfficialListTable
                key='officials'
                entityType={ENTITY_TYPES.org}
                entityId={ORG_ID}
                slug={`/organizations/${ORG_ID}/officials`}
                refreshKey={refreshKey}
                isOrg={true}
              />
            ),
            privatePage: true,
            actions: [
              permission.update && (
                <FileDownload
                  key='download-official'
                  button={(loading, onClick) => {
                    return (
                      <BaseActionButton
                        loading={loading}
                        onClick={onClick}
                        name='download'
                        title={t('common:exportOfficials')}
                      />
                    )
                  }}
                  url={`/organizations/${ORG_ID}/officials/export/csv`}
                  fileName={`${item?.name || item?.name_full}_officials`}
                />
              ),
              permission.update && (
                <ImportOfficials
                  key='import-official'
                  teamName={item?.name || item?.name_full}
                  onSuccess={triggerRefresh}
                  entityType={ENTITY_TYPES.org}
                  entityId={ORG_ID}
                />
              ),
              (isCanlan || features?.rolesPermissions) && (
                <AddOfficial
                  key='add-official'
                  entityType={ENTITY_TYPES.org}
                  entityId={ORG_ID}
                  permission={permission}
                  onComplete={triggerRefresh}
                  isOrg={true}
                />
              ),
            ],
          }
        : null,
    [
      isInFrame,
      permission.admin,
      permission.update,
      ORG_ID,
      item,
      refreshKey,
      triggerRefresh,
      features?.rolesPermissions,
    ]
  )

  const buildUsersPage = useCallback(
    () =>
      !isInFrame && permission.admin
        ? {
            to: `${ROUTES.ORG_ROOT}/${ORG_ID}/users`,
            text: t('common:admin_plural', { count: 2 }),
            component: (
              <UserListTable
                key='users'
                entityType={ENTITY_TYPES.org}
                entityId={ORG_ID}
                slug={`/organizations/${ORG_ID}/permissions`}
                refreshKey={refreshKey}
              />
            ),
            privatePage: true,
            actions: (isCanlan || features?.rolesPermissions) && [
              <ImportCSV
                key='import-users'
                button={(label, onClick) => (
                  <BaseActionButton
                    name='upload'
                    title={label}
                    onClick={onClick}
                  />
                )}
                entityType={ENTITY_TYPES.org}
                id={ORG_ID}
                importType='permissions'
                onSuccess={triggerRefresh}
              />,
              <NewButton
                key='add-user'
                onClick={() => {
                  setEditingUser(null)
                  setIsUserModalOpen(true)
                }}
              />,
            ],
          }
        : null,
    [
      isInFrame,
      ORG_ID,
      refreshKey,
      triggerRefresh,
      features?.rolesPermissions,
      permission,
    ]
  )

  // Reusable dropdown button component
  const createDropdownButton = useCallback(
    (key, buttonId) => (
      <Dropdown
        key={key}
        menu={{ items }}
        placement='bottomLeft'
        trigger={['click']}
      >
        <Button
          id={buttonId}
          css={css`
            margin-left: 8px;
            display: flex;
            justify-content: center;
            align-items: center;
            width: ${64}px;
            height: 32px;

            border: ${isCanlan ? 2 : 1}px solid ${colors.DEFAULT_FLAIR};
            border-radius: 4px;

            ${font.title}
            font-weight: 600;
            font-size: 18px;

            letter-spacing: 0.015em;
            text-transform: uppercase;
            color: white;

            transition: border-color 0.1s ease-in-out;

            &:hover {
              border-color: white;
            }
          `}
        >
          + NEW
        </Button>
      </Dropdown>
    ),
    [items]
  )

  // Common pages for empty org
  const emptyOrgPages = useMemo(
    () =>
      [
        buildEmptyOrgPage(),
        buildReportsPage(),
        buildOfficialsPage(),
        buildUsersPage(),
      ].filter(Boolean),
    [buildEmptyOrgPage, buildReportsPage, buildOfficialsPage, buildUsersPage]
  )

  // Page builders for populated org
  const buildSubOrgsPage = useCallback(
    () =>
      item.can_contain_organizations && entityCount?.org_count > 0
        ? {
            to: `${ROUTES.ORG_ROOT}/${ORG_ID}/children`,
            text: `${t('Web:subOrg_plural', { count: 2 })} ${
              entityCount?.org_count ? `(${entityCount?.org_count})` : ''
            }`,
            component: (
              <OrgListTable
                refreshKey={refreshKey}
                slug={`/organizations/${ORG_ID}/children`}
                setInitialState={(org) =>
                  dispatch({ type: 'SET_ORGANIZATION', payload: org })
                }
                onEditOrg={() => {
                  dispatch({
                    type: 'SET_ORGANIZATION_MODAL_OPEN',
                    payload: true,
                  })
                }}
                onClickWebPluginOnScheduleList={onClickWebPluginOnSubOrgList}
                specificNameColumn='name_full'
              />
            ),
            actions: [
              permission.admin &&
                createDropdownButton('org_dropdown_two', undefined),
            ],
          }
        : null,
    [
      item.can_contain_organizations,
      entityCount?.org_count,
      ORG_ID,
      refreshKey,
      onClickWebPluginOnSubOrgList,
      permission.admin,
      createDropdownButton,
    ]
  )

  const buildSchedulesPage = useCallback(
    () =>
      item.can_contain_schedules && entityCount?.schedule_count > 0
        ? {
            to: `${ROUTES.ORG_ROOT}/${ORG_ID}/schedules`,
            text: `${t('common:competition_plural', { count: 2 })} ${
              entityCount?.schedule_count
                ? `(${entityCount?.schedule_count})`
                : ''
            }`,
            component: (
              <ScheduleListTable
                refreshKey={refreshKey}
                accountTypeId={item?.account_subscription_type_id}
                parentId={ORG_ID}
                slug={`/organizations/${ORG_ID}/schedules`}
                defaultSort='ends_at'
                defaultDirection='desc'
                syncSalesforce={syncSalesforce}
                orgFeatures={features}
                onClickSandboxTour={() => {}}
                onEditSchedule={() => {
                  dispatch({
                    type: 'SET_SCHEDULE_MODAL_OPEN',
                    payload: true,
                  })
                }}
                setInitialState={(schedule) =>
                  dispatch({
                    type: 'SET_SCHEDULE_INITIAL_STATE',
                    payload: schedule,
                  })
                }
                setScheduleInitialStateId={(id) =>
                  dispatch({
                    type: 'SET_SCHEDULE_INITIAL_STATE_ID',
                    payload: id,
                  })
                }
                onClickWebPluginOnScheduleList={onClickWebPluginOnScheduleList}
                isParentOrgProfessionalOrUltimateAccount={
                  isProfessionalOrUltimateAccount
                }
                onClickRegistrationSetup={() => {
                  dispatch({
                    type: 'SET_REGISTRATION_FORM_OPEN',
                    payload: true,
                  })
                }}
                setRegistrationScheduleId={(id) =>
                  dispatch({
                    type: 'SET_REGISTRATION_SCHEDULE_ID',
                    payload: id,
                  })
                }
                onCloneCompetition={(schedule) =>
                  dispatch({
                    type: 'OPEN_CLONE_WIZARD',
                    payload: {
                      id: schedule.id,
                      name_full: schedule.name_full,
                      starts_at: schedule.starts_at,
                      ends_at: schedule.ends_at,
                      is_registration_schedule: schedule.is_registration_on,
                      competition_type: schedule.competition_type,
                    },
                  })
                }
                orgAdminPermission={permission.admin}
                specificNameColumn='name_full'
              />
            ),
            actions: [
              permission.admin &&
                createDropdownButton('org_dropdown_three', undefined),
            ],
          }
        : null,
    [
      item.can_contain_schedules,
      entityCount?.schedule_count,
      ORG_ID,
      refreshKey,
      item?.account_subscription_type_id,
      syncSalesforce,
      features,
      onClickWebPluginOnScheduleList,
      isProfessionalOrUltimateAccount,
      permission.admin,
      createDropdownButton,
    ]
  )

  // "Planet Ice" + "teams" -> "Planet_Ice_teams" (FileDownload appends .csv),
  // the same convention as the roster exports on the team page.
  const orgExportFileName = useCallback(
    (suffix) =>
      `${(item?.name_full || item?.name || 'organization')
        .trim()
        .replace(/\s+/g, '_')}_${suffix}`,
    [item?.name_full, item?.name]
  )

  const buildTeamsPage = useCallback(
    () =>
      item.can_contain_teams && entityCount?.team_count > 0
        ? {
            to: `${ROUTES.ORG_ROOT}/${ORG_ID}/teams`,
            text: `${t('common:team_plural', { count: 2 })} ${
              entityCount?.team_count ? `(${entityCount?.team_count})` : ''
            }`,
            component: (
              <TeamListTable
                refreshKey={refreshKey}
                parentId={ORG_ID}
                slug={`/organizations/${ORG_ID}/teams`}
                soccerSport={soccerSport}
                hockeySport={hockeySport}
                onClickWebPluginOnTeamList={onClickWebPluginOnTeamList}
                isParentOrgProfessionalOrUltimateAccount={
                  isProfessionalOrUltimateAccount
                }
                specificNameColumn='name_full'
              />
            ),
            actions: [
              // The Import/Export dropdowns below trigger these by id; they
              // render their own buttons, so the controls stay hidden.
              permission.update && (
                <div
                  key='teams-import-export-controls'
                  css={css`
                    display: none;
                  `}
                >
                  <ImportCSV
                    key='import-teams'
                    button={(label, onClick) => (
                      <BaseActionButton
                        id='import-teams'
                        name='upload'
                        title={label}
                        onClick={onClick}
                      />
                    )}
                    entityType={ENTITY_TYPES.org}
                    id={ORG_ID}
                    importType={ENTITY_TYPES.team}
                    onSuccess={triggerRefresh}
                  />
                  <FileDownload
                    key='download-teams'
                    button={(loading, onClick) => (
                      <BaseActionButton
                        id='export-teams'
                        loading={loading}
                        onClick={onClick}
                        name='download'
                        title={t('common:exportTeams')}
                      />
                    )}
                    url={`/organizations/${ORG_ID}/teams/export/csv`}
                    fileName={orgExportFileName('teams')}
                  />
                  <ImportTeamRoster
                    key='import-roster'
                    buttonId='import-multi-roster'
                    teamName={item.name_full}
                    onSuccess={triggerRefresh}
                    orgId={ORG_ID}
                  />
                  <FileDownload
                    key='download-multiroster-teams'
                    button={(loading, onClick) => (
                      <BaseActionButton
                        id='export-multi-roster'
                        loading={loading}
                        onClick={onClick}
                        name='export-multi-roster'
                        title='Export Player Rosters'
                      />
                    )}
                    url={`/organizations/${ORG_ID}/teams/exportmulti/csv`}
                    fileName={orgExportFileName('player_rosters')}
                  />
                  <ImportTeamRoster
                    key='import-multi-team-officials'
                    buttonId='import-multi-team-officials'
                    rosterType='teamOfficials'
                    teamName={item.name_full}
                    onSuccess={triggerRefresh}
                    orgId={ORG_ID}
                  />
                  <FileDownload
                    key='download-multi-team-officials'
                    button={(loading, onClick) => (
                      <BaseActionButton
                        id='export-multi-team-officials'
                        loading={loading}
                        onClick={onClick}
                        name='download'
                        title='Export Team Official Rosters'
                      />
                    )}
                    url={`/organizations/${ORG_ID}/teams/team-officials/exportmulti/csv`}
                    fileName={orgExportFileName('team_official_rosters')}
                  />
                </div>
              ),
              permission.update && (
                <Dropdown
                  key='teams-import-dropdown'
                  overlayClassName={actionDropdownOverlay}
                  trigger={['click']}
                  placement='bottomRight'
                  menu={{
                    items: [
                      {
                        key: 'import-teams',
                        label: 'Import Teams',
                        onClick: () =>
                          document.getElementById('import-teams')?.click(),
                      },
                      {
                        key: 'import-multi-roster',
                        label: 'Import Player Rosters',
                        onClick: () =>
                          document
                            .getElementById('import-multi-roster')
                            ?.click(),
                      },
                      {
                        key: 'import-multi-team-officials',
                        label: 'Import Team Official Rosters',
                        onClick: () =>
                          document
                            .getElementById('import-multi-team-officials')
                            ?.click(),
                      },
                    ],
                  }}
                >
                  <span
                    css={css`
                      display: inline-flex;
                    `}
                  >
                    <BaseActionButton
                      customIcon={<ImportIcon size={16} color={colors.WHITE} />}
                    />
                  </span>
                </Dropdown>
              ),
              permission.update && (
                <Dropdown
                  key='teams-export-dropdown'
                  overlayClassName={actionDropdownOverlay}
                  trigger={['click']}
                  placement='bottomRight'
                  menu={{
                    items: [
                      {
                        key: 'export-teams',
                        label: 'Export Teams',
                        onClick: () =>
                          document.getElementById('export-teams')?.click(),
                      },
                      {
                        key: 'export-multi-roster',
                        label: 'Export Player Rosters',
                        onClick: () =>
                          document
                            .getElementById('export-multi-roster')
                            ?.click(),
                      },
                      {
                        key: 'export-multi-team-officials',
                        label: 'Export Team Official Rosters',
                        onClick: () =>
                          document
                            .getElementById('export-multi-team-officials')
                            ?.click(),
                      },
                    ],
                  }}
                >
                  <span
                    css={css`
                      display: inline-flex;
                    `}
                  >
                    <BaseActionButton
                      customIcon={<ExportIcon size={16} color={colors.WHITE} />}
                    />
                  </span>
                </Dropdown>
              ),
              permission.admin && (
                <div id='tutorial-dropdown-teams' key='org_dropdown_div_two'>
                  {createDropdownButton(
                    'tutorial-dropdown-teams',
                    'tutorial-button-teams'
                  )}
                </div>
              ),
            ],
          }
        : null,
    [
      item.can_contain_teams,
      entityCount?.team_count,
      ORG_ID,
      refreshKey,
      soccerSport,
      hockeySport,
      onClickWebPluginOnTeamList,
      isProfessionalOrUltimateAccount,
      item?.name,
      item?.name_full,
      permission.update,
      permission.admin,
      triggerRefresh,
      createDropdownButton,
      orgExportFileName,
    ]
  )

  const buildLocationsPage = useCallback(
    () =>
      item?.can_contain_locations && entityCount?.venue_count > 0
        ? {
            to: `${ROUTES.ORG_ROOT}/${ORG_ID}/locations`,
            text: `${t('common:location_plural', { count: 2 })} ${
              entityCount?.venue_count ? `(${entityCount?.venue_count})` : ''
            }`,
            component: (
              <Venues
                entityType={ENTITY_TYPES.org}
                id={ORG_ID}
                slug={`/organizations/${ORG_ID}/venues`}
                refreshKey={refreshKey}
                triggerRefresh={triggerRefresh}
                parentPermission={permission}
                organizationId={ORG_ID}
              />
            ),
            actions: [
              permission.admin &&
                createDropdownButton('org_dropdown_four', undefined),
            ],
          }
        : null,
    [
      item?.can_contain_locations,
      entityCount?.venue_count,
      ORG_ID,
      refreshKey,
      triggerRefresh,
      permission,
      createDropdownButton,
    ]
  )

  // Print dropdown items for Schedule tab
  const printItems = useMemo(
    () => [
      {
        key: '1',
        label: (
          <a
            css={css`
              cursor: pointer;
            `}
            onClick={() => {
              const currentVisibleGameIds = visibleGameIds
              if (currentVisibleGameIds?.length > 0) {
                const isFirefox = navigator.userAgent.indexOf('Firefox') !== -1
                history.push({
                  pathname: isFirefox
                    ? ROUTES.SCORESHEETS_FIREFOX
                    : ROUTES.SCORESHEETS,
                  state: {
                    games: currentVisibleGameIds,
                    search: window.location.search,
                    isSoccer: false, // Org level doesn't have single sport context
                  },
                })
              }
            }}
          >
            Print Scoresheets
          </a>
        ),
      },
      {
        key: '2',
        label: (
          <a
            css={css`
              cursor: pointer;
            `}
            onClick={() => {
              printSignInSheetModalRef?.current?.openModal()
            }}
          >
            Print Sign-in Sheets
          </a>
        ),
      },
    ],
    [history, visibleGameIds]
  )

  const buildScheduleTabPage = useCallback(
    () =>
      item?.can_contain_games !== false
        ? {
            to: `${ROUTES.ORG_ROOT}/${ORG_ID}/games`,
            text: 'Schedule',
            component: (
              <OrgSchedule
                orgId={ORG_ID}
                setVisibleGameIds={setVisibleGameIds}
              />
            ),
            actions: [
              <Dropdown
                menu={{ items: printItems }}
                placement='bottomLeft'
                trigger={['click']}
                key='print-dropdown'
              >
                <div
                  css={css`
                    cursor: pointer;
                    border: 1px solid ${colors.DEFAULT_FLAIR};
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    padding-top: 4px;
                    padding-bottom: 4px;
                    padding-left: 8px;
                    padding-right: 8px;
                    margin-left: 8px;
                    border-radius: 4px;
                    &:hover {
                      border: 1px solid ${colors.NEUTRAL_0};
                    }
                  `}
                >
                  <PrinterOutlined
                    style={{
                      color: colors.WHITE,
                    }}
                  />
                </div>
              </Dropdown>,
            ],
          }
        : null,
    [ORG_ID, printItems, setVisibleGameIds, item?.can_contain_games]
  )

  const buildLeaderboardsPage = useCallback(
    () =>
      false
        ? {
            to: `${ROUTES.ORG_ROOT}/${ORG_ID}/leaderboards`,
            text: 'Leaderboards',
            component: (
              <OrgLeaderboards orgId={ORG_ID} refreshKey={refreshKey} />
            ),
          }
        : null,
    [ORG_ID, refreshKey]
  )

  const buildFeedPage = useCallback(
    () =>
      features?.announcements && item?.can_contain_feeds !== false
        ? {
            to: `${ROUTES.ORG_ROOT}/${ORG_ID}/feed`,
            text: t('common:feed'),
            component: (
              <EntityFeed
                entity={item}
                entityType={ENTITY_TYPES.org}
                entityId={ORG_ID}
                refreshKey={refreshKey}
                canPost={
                  permission.post || permission.update || permission.admin
                }
                canTargetAudience={permission.update || permission.admin}
              />
            ),
            actions: [
              permission.admin && (
                <Mobile key='add-post'>
                  <FormSheet
                    label={t('Web:createANewPost')}
                    title={t('Web:createANewPost')}
                    form={feedItemForm({}, false)}
                    onSubmit={async ({ imageData, ...form }) => {
                      const body = JSON.stringify(form)
                      const response = await req(
                        `/organizations/${ORG_ID}/social-feed`,
                        {
                          method: 'POST',
                          body,
                        }
                      )
                      if (imageData) {
                        await req(`/social-feed/${response.data.id}/image`, {
                          method: 'POST',
                          body: imageData,
                        })
                      }
                      triggerRefresh()
                    }}
                    Button={({ onClick }) => <NewButton onClick={onClick} />}
                  />
                </Mobile>
              ),
            ],
          }
        : null,
    [
      features?.announcements,
      ORG_ID,
      item,
      refreshKey,
      triggerRefresh,
      permission.update,
      permission.post,
      permission.admin,
      item?.can_contain_feeds,
    ]
  )

  const buildCouponsPage = useCallback(
    () =>
      !isInFrame && permission?.admin
        ? {
            to: `${ROUTES.ORG_ROOT}/${ORG_ID}/coupons`,
            text: 'Coupons',
            privatePage: true,
            component: (
              <CouponsPage
                organizationId={ORG_ID}
                openCreateModalRef={openCouponCreateRef}
              />
            ),
            actions: [
              <NewButton
                key='add-coupon'
                onClick={() => openCouponCreateRef.current?.()}
              />,
            ],
          }
        : null,
    [isInFrame, permission?.admin, ORG_ID]
  )

  // Populated org pages
  const populatedOrgPages = useMemo(
    () =>
      [
        buildSubOrgsPage(),
        buildSchedulesPage(),
        buildScheduleTabPage(),
        buildLeaderboardsPage(),
        buildTeamsPage(),
        buildLocationsPage(),
        buildFeedPage(),
        buildCouponsPage(),
        buildReportsPage(),
        buildOfficialsPage(),
        buildUsersPage(),
      ].filter(Boolean),
    [
      buildSubOrgsPage,
      buildSchedulesPage,
      buildTeamsPage,
      buildScheduleTabPage,
      buildLeaderboardsPage,
      buildLocationsPage,
      buildFeedPage,
      buildCouponsPage,
      buildReportsPage,
      buildOfficialsPage,
      buildUsersPage,
    ]
  )

  const titleActions = useMemo(
    () =>
      item?.id
        ? [
            <FavouriteButton
              key='favourite'
              initialState={item.is_favorite}
              entityType={ENTITY_TYPES.org}
              entityId={ORG_ID}
            />,
            permission.update && (
              <EditButton
                key='org_edit_button'
                onClick={() => {
                  dispatch({ type: 'SET_ORGANIZATION', payload: item })
                  dispatch({
                    type: 'SET_ORGANIZATION_MODAL_OPEN',
                    payload: true,
                  })
                }}
                title={item?.name || item?.name_full}
              />
            ),
          ]
        : [],
    [item, ORG_ID, permission.update]
  )

  const baseActions = useMemo(
    () => [
      permission.admin && item?.integrations?.daysmart?.is_connected && (
        <BaseActionButton
          key='import-daysmart'
          loading={loading}
          onClick={() => {
            daySmartImportModalRef?.current?.openModal()
          }}
          name='upload'
          title={'Import DaySmart Season'}
          customIcon={
            <img
              src={daysmartIcon}
              css={css`
                width: 16px;
                height: 16px;
              `}
            />
          }
        />
      ),
      shouldDisplayMergeButton && (
        <Dropdown
          key='merge-dropdown'
          menu={{
            items: [
              {
                key: 'merge-players',
                label: t('Web:mergePlayers'),
                onClick: () => {
                  history.push(ROUTES.MERGE_PLAYERS, {
                    organizationName: item?.name || item?.name_full,
                    organizationId: ORG_ID,
                  })
                },
              },
              {
                key: 'merge-teams',
                label: 'Merge Teams',
                onClick: () => {
                  history.push(ROUTES.MERGE_TEAMS, {
                    organizationName: item?.name || item?.name_full,
                    organizationId: ORG_ID,
                  })
                },
              },
            ],
          }}
          trigger={['click']}
        >
          <BaseActionButton
            key='merge'
            name='code-merge'
            title='Merge'
            placement='top'
          />
        </Dropdown>
      ),
      permission?.update && isProfessionalOrUltimateAccount && (
        <BaseActionButton
          key='plug'
          name='plug'
          title={'Generate JS Plugin Tag'}
          onClick={() => {
            generatePluginTagModalRef?.current?.openModal()
          }}
        />
      ),
    ],
    [
      permission.admin,
      permission?.update,
      item?.integrations?.daysmart?.is_connected,
      item?.name,
      item?.name_full,
      loading,
      shouldDisplayMergeButton,
      isProfessionalOrUltimateAccount,
      history,
      ORG_ID,
    ]
  )

  // Optimized pages variable - uses smaller, focused builders
  const pages = useMemo(
    () => (isEmptyOrg ? emptyOrgPages : populatedOrgPages),
    [isEmptyOrg, emptyOrgPages, populatedOrgPages]
  )

  return (
    <>
      <Page
        contactInfo={{
          support_email: item?.support_email,
          support_website: item?.support_website,
          support_phone: item?.support_phone,
          email: item?.email,
          website: item?.website,
          phone: item?.phone,
        }}
        backgroundImageUrl={resolveAssetUrl(item?.background_image)}
        error={error}
        loading={loading}
        baseCrumb={ROUTES.ORGS.ROOT}
        baseRoute={BASE_ROUTE}
        baseTitle={t('common:organization_plural', { count: 2 })}
        breadcrumbs={getBreadcrumbs(ROUTES.ORG_ROOT, item, hierarchy)}
        baseActions={baseActions}
        pages={pages}
        isPrivate={!IS_PUBLIC}
        title={item?.name || item?.name_full}
        titleActions={titleActions}
        imageId={getImageThumbnailId(item)}
        isOrganization={true}
        organizationId={item?.id}
        organization={item}
        organizationPermission={permission}
        onOnboardingChanged={handleSportsPayOnboardingChanged}
      />
      <a
        id='hidden-location-tutorial'
        target='_blank'
        rel='noopener noreferrer'
        onClick={() => {}}
      />
      <a
        id='hidden-schedule-tutorial'
        target='_blank'
        rel='noopener noreferrer'
        onClick={() => {}}
      />
      <LocationModal
        isOpen={isLocationModalOpen}
        onClose={() =>
          dispatch({ type: 'SET_LOCATION_MODAL_OPEN', payload: false })
        }
        organizationId={ORG_ID}
        onSuccess={() => {
          triggerRefresh()
          readyEntityCount()
          dispatch({ type: 'SET_LOCATION_MODAL_OPEN', payload: false })
          const button = document.getElementById('hidden-schedule-tutorial')
          button?.click()
        }}
      />
      <NewModal
        ref={daySmartImportModalRef}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEscPreventDefault={false}
      >
        <NewDaySmartForm
          daySmartImportModalRef={daySmartImportModalRef}
          readItem={readItem}
          readyEntityCount={readyEntityCount}
          key={Math?.random()?.toString()}
          account={account}
          account_id={item?.account_id}
          ORG_ID={ORG_ID}
          genericSports={genericSportsForDaysmart}
          sports={sportsForDaysmart}
        />
      </NewModal>
      <CreateCompetitionWizard
        isOpen={isCreateWizardOpen}
        orgId={ORG_ID}
        orgEmail={item?.email}
        orgWebsite={item?.website}
        cloneInitialSchedule={cloneInitialSchedule}
        onClose={() =>
          dispatch({ type: 'SET_CREATE_WIZARD_OPEN', payload: false })
        }
        onSkipToLegacy={() => {
          dispatch({ type: 'SET_CREATE_WIZARD_OPEN', payload: false })
          dispatch({ type: 'RESET_SCHEDULE_FORM', payload: true })
        }}
      />
      <ScheduleModal
        setNewCompetitionId={(id) =>
          dispatch({ type: 'SET_NEW_COMPETITION_ID', payload: id })
        }
        setIsCompetitionSetupModalOpen={(open) =>
          dispatch({
            type: 'SET_COMPETITION_SETUP_MODAL_OPEN',
            payload: open,
          })
        }
        isFormOpen={isScheduleModalOpen}
        setIsFormOpen={(open) =>
          dispatch({ type: 'SET_SCHEDULE_MODAL_OPEN', payload: open })
        }
        initialState={scheduleInitialState || null}
        onCancel={handleScheduleModalCancel}
        triggerRefresh={handleScheduleModalTriggerRefresh}
        parentOrgId={ORG_ID}
        displayCanlanForm={syncSalesforce}
        addPending={addPending}
        onFinishTutorial={() => {}}
        isDaysmartConnectedAtOrgLevel={isDaysmartConnectedAtOrgLevel}
        sportId={item.sport_id}
      />
      <NewModal
        ref={generatePluginTagModalRef}
        shouldCloseOnOverlayClick={false}
      >
        <GeneratePluginModal
          onPressClose={handlePluginModalClose}
          scheduleId={
            selectedSchedule?.id ||
            selectedSubOrg ||
            selectedTeam?.id ||
            item?.id
          }
          isOrganization={isOrganization(
            selectedSchedule,
            selectedTeam,
            selectedSubOrg
          )}
          isTeam={selectedTeam}
        />
      </NewModal>
      <ReprocessingSettingsOverlay
        scheduleReprocessingListener={scheduleReprocessingListener}
        setOutsideDismissed={setDismissed}
        dismissed={dismissed}
      />
      <OrganizationModal
        isFormOpen={organizationModalOpen}
        setIsFormOpen={(open) =>
          dispatch({ type: 'SET_ORGANIZATION_MODAL_OPEN', payload: open })
        }
        organization={organization}
        account_id={account && JSON.parse(JSON.stringify(account?.uid))}
        setOrganization={(org) =>
          dispatch({ type: 'SET_ORGANIZATION', payload: org })
        }
        refresh={handleOrganizationModalRefresh}
        parentOrgId={ORG_ID}
      />
      <RegistrationSetup
        isRegistrationFormOpen={isRegistrationFormOpen}
        setIsRegistrationFormOpen={(open) =>
          dispatch({ type: 'SET_REGISTRATION_FORM_OPEN', payload: open })
        }
        registration={data?.detailed?.registration}
        registrationTypes={types}
        availableTaxes={availableTaxes}
        scheduleId={registrationScheduleId}
        refetchRegistrationFn={refetchRegistrationFn}
      />
      <CompetitionSetupModal
        isOpen={isCompetitionSetupModalOpen}
        setIsOpen={(open) =>
          dispatch({
            type: 'SET_COMPETITION_SETUP_MODAL_OPEN',
            payload: open,
          })
        }
        onContinue={handleCompetitionSetupModalContinue}
        onCancel={handleCompetitionSetupModalCancel}
      />
      <TeamModal
        isOpen={isTeamModalOpen}
        onClose={() =>
          dispatch({ type: 'SET_TEAM_MODAL_OPEN', payload: false })
        }
        organizationId={ORG_ID}
        onSuccess={() => {
          triggerRefresh()
          readyEntityCount()
        }}
        isParentPublic={IS_PUBLIC}
        permission={permission}
      />
      <NewModal
        ref={printSignInSheetModalRef}
        shouldCloseOnOverlayClick={false}
      >
        <PrintSignSheetsModal
          onPressClose={() => printSignInSheetModalRef?.current?.closeModal()}
          visibleGameIds={visibleGameIds}
          history={history}
        />
      </NewModal>
      <UserModal
        isOpen={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
        initialData={editingUser}
        entityType={ENTITY_TYPES.org}
        entityId={ORG_ID}
        onSuccess={() => {
          triggerRefresh()
          setIsUserModalOpen(false)
          setEditingUser(null)
        }}
      />
    </>
  )
}

const mapStateToProps = (state) => {
  return {
    hockeySport: getHockeySport(state),
    soccerSport: getSoccerSport(state),
    isInFrame: state.auth.inFrame,
    genericSportsForDaysmart: getAllGenericSportProperties(state),
    sportsForDaysmart: Object.values(getSports(state)),
  }
}

export default connect(mapStateToProps)(Organization)
